import { useAuth } from '@/components/auth/authProvider';
import { isPalmettoFinanceUser } from '@/components/auth/RequirePalmettoFinanceUser';
import { ResettingFileUpload } from '@/components/FileUpload/ResettingFileUpload';
import { getPreviewUrl, getOriginalUrl } from '@/helpers/documentHelpers';
import { intercomTarget } from '@/helpers/intercomTarget';
import { uploadDocumentsWithToast } from '@/helpers/uploadDocumentsWithToast';
import { useGetDocumentsQuery, useUploadDocumentMutation, useWatchDocumentVariants } from '@/services/documents';
import { useGetDocumentExtractQuery } from '@/services/genai';
import { useGetQueueByAccountIdAndTypeQuery } from '@/services/queues';
import { Box, Card, Icon, Spinner, TextLink, useBreakpoint } from '@palmetto/palmetto-components';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { createRef, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
  AllDocumentTypes,
  DocumentSortField,
  DocumentStatus,
  MilestonePackageRoute,
  MilestoneRouteToQueueTypeMap,
  PACKAGE_RESTRICTED_ACTIONS_QUEUE_STATUSES,
  ProgramType,
  RequirementFlagSettingsType,
  supportedDocumentExtractTypes,
  UserPermissions,
} from 'types';

import { ReviewPackagePhotoCategory } from '../../../types/ReviewPackage';
import RequirePermissions from '../../auth/requirePermissions';
import { DocumentPreview, DocumentPreviewModal } from '../../documents/DocumentPreviewModal';
import { Flag } from '../../QueueFlags/Flag';
import { FlagInfo } from '../../QueueFlags/FlagInfo';
import { QueueFlagsContext } from '../../QueueFlags/QueueFlagsContext';

import CheckboxHeader from './CheckboxHeader';
import { DocumentListItem } from './DocumentListItem';

type PhotoListProps = {
  additionalDocumentsToFetch?: Array<{
    types: Array<AllDocumentTypes> | undefined;
    status?: Array<DocumentStatus>;
  }>;
  showDocumentActions?: boolean;
  showCheckboxes?: boolean;
  allowArchive?: boolean;
  showUploadButton?: boolean;
  showIfEmpty?: boolean;
  checkboxCategories?: Array<AllDocumentTypes>;
  category: ReviewPackagePhotoCategory;
  documentTypeFilter?: Array<AllDocumentTypes>;
  className?: string;
  description?: string;
  documentationUrl?: string;
  title?: string;
  baseCategory?: string;
  flagType: RequirementFlagSettingsType;
  programType: ProgramType;
  isPackageEditable?: boolean;
};

type UploadButtonProps = {
  isPhone: boolean;
  category: string;
  handleFileUpload: (event: any) => void;
  isPackageEditable?: boolean;
};

const UploadButton = ({ isPhone, category, handleFileUpload, isPackageEditable = true }: UploadButtonProps) => (
  <ResettingFileUpload
    labelText="Upload"
    id={`upload-${category}-photos`}
    name={`${category}-files`}
    size="sm"
    onChange={handleFileUpload}
    multiple
    buttonText={!isPhone ? 'Upload' : null}
    onClick={(event: any) => event.stopPropagation()}
    isDisabled={!isPackageEditable}
  />
);

const TitleWithCount = ({
  title,
  documentsLength,
  category,
  baseCategory,
}: {
  title: string;
  documentsLength: number;
  category: ReviewPackagePhotoCategory;
  baseCategory?: string;
}) => (
  <Box
    data-intercom-target={intercomTarget(baseCategory, category)}
    as="h3"
    fontWeight="medium"
    fontSize="md"
    direction="row"
    gap="md"
    alignItems="center"
  >
    <Box>{title ? title : 'Documents'}</Box>
    {documentsLength > 1 && <Box fontSize="md" fontWeight="light">{`${documentsLength} files`}</Box>}
  </Box>
);

const Description = ({ description, documentationUrl }: { description?: string; documentationUrl?: string }) => (
  <Box as="p">
    {description}
    {documentationUrl && (
      <TextLink href={documentationUrl} rel="noreferrer" target="_blank">
        See full requirements
      </TextLink>
    )}
  </Box>
);

export function PhotosList({
  showDocumentActions = true,
  allowArchive = true,
  showUploadButton = true,
  showCheckboxes = false,
  showIfEmpty = true,
  checkboxCategories = [],
  category,
  documentTypeFilter,
  className,
  description,
  documentationUrl,
  title = '',
  baseCategory,
  flagType,
  isPackageEditable = true,
}: PhotoListProps) {
  const { flagDrivenPackageReview } = useFlags();
  const { id } = useParams<{ id: any }>();
  const queryParams = new URLSearchParams(useLocation().search);
  const scrollTo = queryParams.get('scrollTo');
  const { data: documents = [], isLoading } = useGetDocumentsQuery({
    accountId: id,
    documentTypeFilter: documentTypeFilter ?? [category],
    sort: DocumentSortField.ID_DESC,
  });
  const { isPhone } = useBreakpoint();
  const { getFlag, getFlagsByFlagType, shouldHide } = useContext(QueueFlagsContext);
  const currentFlag = getFlag(title, undefined, false);
  const currentFlags = flagType ? getFlagsByFlagType(flagType, false, title) : [];
  const allFlags = [...(currentFlags ? currentFlags : []), ...(currentFlag ? [currentFlag] : [])];
  const hasFlags = flagDrivenPackageReview ? Boolean(allFlags?.length) : Boolean(currentFlag);
  const [uploadDocument] = useUploadDocumentMutation();
  // Track the most recent v2 upload job so its thumbnail/preview variants pop
  // in automatically when the worker finishes (no manual refresh). The LIST
  // invalidation refreshes all docs regardless of which upload it came from.
  const [variantJobId, setVariantJobId] = useState<string | undefined>(undefined);
  useWatchDocumentVariants({ accountId: id, jobId: variantJobId ?? '' }, { skip: !variantJobId });
  const [checkedItems, setCheckedItems] = useState<string[]>([]);
  const filteredCheckboxCategories = useMemo(
    () => checkboxCategories.filter((checkboxCategory) => checkboxCategory !== category),
    [category, checkboxCategories],
  );

  const sectionRefs = useMemo(
    () => ({
      documents: createRef(),
    }),
    [],
  ) as any;

  useEffect(() => {
    if (scrollTo) {
      sectionRefs[scrollTo]?.current?.scrollIntoView();
    }
  }, [scrollTo, sectionRefs]);

  const handleFileUpload = useCallback(
    async (event: any) => {
      const files: FileList | undefined = event.target?.files;
      if (!files || !files.length) return;

      const data = new FormData();
      data.append('type', category);

      for (let i = 0; i < files.length; i++) {
        data.append(`files[${i}]`, files[i]);
      }

      try {
        const { jobId } = await uploadDocumentsWithToast({
          uploadDocument,
          accountId: id,
          formData: data,
          invalidateAllTags: false,
          files,
          messages: {
            success: (count) => (count === 1 ? `${title} photo uploaded` : `${count} ${title} photos uploaded`),
            fallbackError: 'An error occurred while uploading photos',
          },
        });
        if (jobId) setVariantJobId(jobId);
      } catch {
        // Upload failed — the helper already surfaced an error toast.
      }
    },
    [category, id, title, uploadDocument],
  );

  if (!showIfEmpty && !isLoading && documents.length === 0) {
    return null;
  }

  const renderFlaggedContent = () => {
    if (!hasFlags) return null;

    if (flagDrivenPackageReview) {
      return (
        <Box width="100">
          <FlagInfo dataKey={title} flagType={flagType} hasFlags={hasFlags} />
        </Box>
      );
    }

    return (
      <Box direction="row" alignItems="center">
        <Box display="block" childGap="xs" padding={{ base: 'md lg', tablet: 'sm md md 0' }}>
          <Icon name="flag" size="lg" color="danger" />
        </Box>
        <Box
          display="block"
          padding={{ base: '0 lg', tablet: 'md md md 0' }}
          margin="0 2xl 0 0"
          width={{ base: '2xl' }}
        >
          <FlagInfo dataKey={title} flagType={flagType} hasFlags={hasFlags} />
        </Box>
      </Box>
    );
  };

  const renderContentSection = () => {
    const uploadButtonContent = showUploadButton && (
      <RequirePermissions permissions={[UserPermissions.admin, UserPermissions.editor]} checkAllPermissions={false}>
        <UploadButton
          isPhone={isPhone}
          category={category}
          handleFileUpload={handleFileUpload}
          isPackageEditable={isPackageEditable}
        />
      </RequirePermissions>
    );

    const flagButton = !shouldHide && title && (
      <Flag dataKey={title} flagType={flagType} hasFlags={hasFlags} type={category} />
    );

    if (flagDrivenPackageReview) {
      return (
        <Box
          childGap="lg"
          padding="lg"
          alignItems="center"
          direction="column"
          ref={sectionRefs.documents}
          borderColor={hasFlags ? 'danger-500' : 'separator'}
          borderWidth={hasFlags ? '0 0 0 md' : '0'}
        >
          {hasFlags && renderFlaggedContent()}
          <Box width="100" direction="row" gap="lg" justifyContent="space-between">
            <Box gap="sm">
              <TitleWithCount
                category={category}
                baseCategory={baseCategory}
                title={title}
                documentsLength={documents?.length}
              />
              <Description description={description} documentationUrl={documentationUrl} />
            </Box>
            <Box direction="row" gap="md" alignItems="center">
              {flagButton}
              {uploadButtonContent}
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box
        childGap="lg"
        padding="lg"
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        ref={sectionRefs.documents}
      >
        {hasFlags && renderFlaggedContent()}
        <Box gap="sm">
          <TitleWithCount
            category={category}
            baseCategory={baseCategory}
            title={title}
            documentsLength={documents?.length}
          />
          <Description description={description} documentationUrl={documentationUrl} />
        </Box>
        <Box direction="row" gap="md" alignItems="center">
          {flagButton}
          {uploadButtonContent}
        </Box>
      </Box>
    );
  };

  return (
    <Card className={className} background={hasFlags ? 'danger' : 'primary'} overflow="visible">
      <Box className={`photos-list-header ${hasFlags ? 'flagged' : ''} ${checkedItems.length ? 'checked' : ''}`}>
        {renderContentSection()}
        {checkedItems.length ? (
          <CheckboxHeader
            documents={documents}
            checkboxCategories={filteredCheckboxCategories}
            allowArchive={allowArchive}
            checkedItems={checkedItems}
            setCheckedItems={setCheckedItems}
          />
        ) : null}
      </Box>
      <List
        isLoading={isLoading}
        items={documents}
        showDocumentActions={showDocumentActions}
        showCheckboxes={showCheckboxes}
        allowArchive={allowArchive}
        baseCategory={baseCategory}
        flagType={flagType}
        checkedItems={checkedItems}
        setCheckedItems={setCheckedItems}
        isPackageEditable={isPackageEditable}
      />
    </Card>
  );
}

interface ListProps {
  isLoading: boolean;
  items: any[];
  showDocumentActions?: boolean;
  showCheckboxes?: boolean;
  checkboxCategories?: Array<AllDocumentTypes>;
  allowArchive?: boolean;
  baseCategory?: string;
  title?: string;
  flagType: RequirementFlagSettingsType;
  checkedItems: string[];
  setCheckedItems: React.Dispatch<React.SetStateAction<string[]>>;
  isPackageEditable?: boolean;
}

/** Threshold below which we skip virtualization for the document list. */
const LIST_VIRTUALIZATION_THRESHOLD = 20;

/** Default estimated height for a document list row in pixels. */
const LIST_ESTIMATED_ROW_HEIGHT = 72;

/** Number of extra rows to render above/below visible area for variable-height items. */
const LIST_OVERSCAN_COUNT = 25;

/** Maximum height of the scrollable list container. */
const LIST_MAX_HEIGHT = '70vh';

interface VirtualDocumentListItemProps {
  item: any;
  showDocumentActions: boolean;
  showCheckboxes: boolean;
  setCheckedItems: React.Dispatch<React.SetStateAction<string[]>>;
  checkedItems: string[];
  allowArchive: boolean;
  baseCategory?: string;
  flagType: RequirementFlagSettingsType;
  flagDrivenPackageReview: boolean;
  showFlagToEPC: boolean;
  togglePreviewModal: (id: string) => void;
  togglePreviewModalFlagDrivenPackageReview: (itemId: string, fileIndex: number) => void;
  isPackageEditable?: boolean;
}

const VirtualDocumentListItem = memo(
  ({
    item,
    showDocumentActions,
    showCheckboxes,
    setCheckedItems,
    checkedItems,
    allowArchive,
    baseCategory,
    flagType,
    flagDrivenPackageReview,
    showFlagToEPC,
    togglePreviewModal,
    togglePreviewModalFlagDrivenPackageReview,
    isPackageEditable = true,
  }: VirtualDocumentListItemProps) => {
    const handleClick = useCallback(() => {
      if (flagDrivenPackageReview) {
        togglePreviewModalFlagDrivenPackageReview(item.id, 0);
      } else {
        togglePreviewModal(item.id);
      }
    }, [item.id, flagDrivenPackageReview, togglePreviewModal, togglePreviewModalFlagDrivenPackageReview]);

    return (
      <DocumentListItem
        item={item}
        showDocumentActions={showDocumentActions}
        showCheckboxes={showCheckboxes}
        setCheckedItems={setCheckedItems}
        checkedItems={checkedItems}
        allowArchive={allowArchive}
        showTypeLabel={false}
        baseCategory={baseCategory}
        flagType={flagType}
        showFlagToEPC={showFlagToEPC}
        onClick={handleClick}
        isPackageEditable={isPackageEditable}
      />
    );
  },
);

function List({
  isLoading,
  items,
  showDocumentActions = true,
  showCheckboxes = false,
  checkedItems,
  setCheckedItems,
  allowArchive = true,
  baseCategory,
  title,
  flagType,
  isPackageEditable = true,
}: ListProps) {
  const [isFilePreviewModalOpen, setIsFilePreviewModalOpen] = useState(false);
  const { getFlag } = useContext(QueueFlagsContext);
  const { flagDrivenPackageReview, enableCustomPdfViewer = false } = useFlags();
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const milestonePackageURI = location.pathname.split('/')[3];
  const queueType = MilestoneRouteToQueueTypeMap[milestonePackageURI as MilestonePackageRoute];
  const { data: queue } = useGetQueueByAccountIdAndTypeQuery(
    { accountId: id, type: queueType },
    { skip: !id || !queueType || !flagDrivenPackageReview },
  );
  const showFlagToEPC = flagDrivenPackageReview
    ? !(queue?.status && PACKAGE_RESTRICTED_ACTIONS_QUEUE_STATUSES.has(queue?.status) && !isFinCoUser)
    : true;
  const totalCount = items?.length || 0;
  const shouldVirtualize = totalCount > LIST_VIRTUALIZATION_THRESHOLD;
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const documents: DocumentPreview[] = useMemo(
    () =>
      items?.map((item) => {
        const viewUrls = item.files[0]?.viewUrls;
        return {
          id: item.id,
          documentId: item.id,
          url: getPreviewUrl(viewUrls) ?? '',
          originalUrl: getOriginalUrl(viewUrls),
          contentType: item.files[0]?.contentType,
          fileName: item.files[0]?.originalName,
          createdAt: item.meta?.createdAt,
          type: item.type,
        };
      }),
    [items],
  );

  const documentsFlagDrivenPackageReview: DocumentPreview[] = useMemo(
    () =>
      items?.flatMap((item) =>
        item.files.map((file: any, index: number) => {
          const indexedId = `${item.id}-${index}`;
          const flaggedItem = getFlag([item.id], file.originalName);
          const flaggedIndexedItem = getFlag([indexedId], file.originalName);
          const id = flaggedItem ? item.id : flaggedIndexedItem ? indexedId : `${item.id}-${index}`;
          const viewUrls = file?.viewUrls;

          return {
            id: id,
            documentId: item.id,
            url: getPreviewUrl(viewUrls) ?? '',
            originalUrl: getOriginalUrl(viewUrls),
            contentType: file?.contentType,
            fileName: file?.originalName,
            createdAt: item.meta?.createdAt,
            type: item.type,
            pageNumber: index + 1,
          };
        }),
      ),
    [items, getFlag],
  );

  const [targetDoc, setTargetDoc] = useState<DocumentPreview>(documents[0]);

  const isTargetDocPdfOrImage =
    targetDoc?.contentType === 'application/pdf' || Boolean(targetDoc?.contentType?.startsWith('image/'));
  const isTargetDocExtractSupported = supportedDocumentExtractTypes.includes(targetDoc?.type as AllDocumentTypes);
  const { currentData: targetDocExtract } = useGetDocumentExtractQuery(targetDoc?.documentId ?? '', {
    skip:
      !isFilePreviewModalOpen ||
      !targetDoc?.documentId ||
      !enableCustomPdfViewer ||
      !isTargetDocPdfOrImage ||
      !isTargetDocExtractSupported,
  });
  const documentLayout = targetDocExtract?.response?.documentLayout;

  const togglePreviewModal = useCallback(
    (id: string) => {
      const targetDocument = documents.find((doc) => doc.id === id);
      setTargetDoc(targetDocument ?? documents[0]);
      setIsFilePreviewModalOpen(true);
    },
    [documents],
  );

  const togglePreviewModalFlagDrivenPackageReview = useCallback(
    (itemId: string, fileIndex: number) => {
      const documentId = `${itemId}-${fileIndex}`;
      const targetItem = items.find((item) => item.id === itemId);
      if (!targetItem) {
        return;
      }

      const targetFile = targetItem.files[fileIndex];
      if (!targetFile) {
        return;
      }

      const flaggedItem = getFlag([itemId], targetFile.originalName);
      const flaggedIndexedItem = getFlag([documentId], targetFile.originalName);
      const currentItemId = flaggedItem ? itemId : flaggedIndexedItem ? documentId : documentId;

      const targetPreview = documentsFlagDrivenPackageReview.find((doc) => doc.id === currentItemId);
      if (targetPreview) {
        setTargetDoc(targetPreview);
        setIsFilePreviewModalOpen(true);
      }
    },
    [documentsFlagDrivenPackageReview, items, getFlag],
  );

  const closeFilePreviewModal = useCallback(() => setIsFilePreviewModalOpen(false), []);

  const estimateSize = useCallback(() => LIST_ESTIMATED_ROW_HEIGHT, []);

  const virtualizer = useVirtualizer({
    count: totalCount,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize,
    overscan: LIST_OVERSCAN_COUNT,
    enabled: shouldVirtualize,
  });

  const renderItems = () => {
    if (!shouldVirtualize) {
      return items.map((item) => (
        <VirtualDocumentListItem
          key={item.id}
          item={item}
          showDocumentActions={showDocumentActions}
          showCheckboxes={showCheckboxes}
          setCheckedItems={setCheckedItems}
          checkedItems={checkedItems}
          allowArchive={allowArchive}
          baseCategory={baseCategory}
          flagType={flagType}
          flagDrivenPackageReview={flagDrivenPackageReview}
          showFlagToEPC={showFlagToEPC}
          togglePreviewModal={togglePreviewModal}
          togglePreviewModalFlagDrivenPackageReview={togglePreviewModalFlagDrivenPackageReview}
          isPackageEditable={isPackageEditable}
        />
      ));
    }

    const virtualItems = virtualizer.getVirtualItems();
    const totalSize = virtualizer.getTotalSize();
    const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
    const paddingBottom =
      virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1].end : totalSize;

    return (
      <div
        ref={scrollContainerRef}
        style={{
          maxHeight: LIST_MAX_HEIGHT,
          overflowY: 'auto',
        }}
      >
        <div style={{ paddingTop: `${paddingTop}px`, paddingBottom: `${paddingBottom}px` }}>
          {virtualItems.map((virtualItem) => {
            const item = items[virtualItem.index];
            return (
              <div key={virtualItem.key} data-index={virtualItem.index} ref={virtualizer.measureElement}>
                <VirtualDocumentListItem
                  item={item}
                  showDocumentActions={showDocumentActions}
                  showCheckboxes={showCheckboxes}
                  setCheckedItems={setCheckedItems}
                  checkedItems={checkedItems}
                  allowArchive={allowArchive}
                  baseCategory={baseCategory}
                  flagType={flagType}
                  flagDrivenPackageReview={flagDrivenPackageReview}
                  showFlagToEPC={showFlagToEPC}
                  togglePreviewModal={togglePreviewModal}
                  togglePreviewModalFlagDrivenPackageReview={togglePreviewModalFlagDrivenPackageReview}
                  isPackageEditable={isPackageEditable}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      <Card.Section padding="0">
        {totalCount > 0 ? (
          <>
            <DocumentPreviewModal
              targetDoc={targetDoc}
              setTargetDoc={setTargetDoc}
              isFilePreviewModalOpen={isFilePreviewModalOpen}
              documentList={flagDrivenPackageReview ? documentsFlagDrivenPackageReview : documents}
              closeFilePreviewModal={closeFilePreviewModal}
              title={title}
              baseCategory={baseCategory}
              flagType={flagType}
              enableCustomPdfViewer={enableCustomPdfViewer}
              documentLayout={documentLayout}
            />
            {renderItems()}
          </>
        ) : (
          <Box
            display="block"
            textAlign="center"
            padding="lg"
            childGap="xl"
            borderColor="separator"
            borderWidth="xs 0 0 0"
          >
            {isLoading ? (
              <Spinner size="lg" />
            ) : (
              <>
                <Box fontSize="sm" color="body-secondary">
                  No documents exist for this account
                </Box>
              </>
            )}
          </Box>
        )}
      </Card.Section>
    </>
  );
}
